import React, { HTMLAttributes } from 'react';
import ReactDOM from 'react-dom';
import { Component } from '../types';
import { Providers } from './providers';

type MFImporterProps = {
  readonly reactDOM?: () => Promise<{
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore Cannot find module 'react-dom/client' or its corresponding type declarations.ts(2307)
    default: typeof ReactDOM | typeof import('react-dom/client');
  }>;
  readonly component: () => Promise<{
    default: Component;
  }>;
  readonly componentContainer?: Component;
  readonly useProviders?: boolean;
  readonly wrapperStyle?: Record<string, string>;
};

function defaultContainer(props: HTMLAttributes<HTMLDivElement>) {
  return <div {...props} />;
}

export class MFImporter extends React.Component<
  MFImporterProps & Record<string, unknown>
> {
  private div: HTMLDivElement | null = null;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore Cannot find module 'react-dom/client' or its corresponding type declarations.ts(2307)
  private root: import('react-dom/client').Root | null = null;

  // unMountFn will be called to remove the remote component from the DOM during unmount
  private unMountFn?: () => void;

  componentDidMount() {
    this.init();
  }

  componentWillUnmount() {
    this.unMountFn?.();
  }

  shouldComponentUpdate(nP) {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      reactDOM,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      component,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      useProviders,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      componentContainer,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      wrapperStyle,
      ...componentProps
    } = this.props;

    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      reactDOM: _reactDOM,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      component: _component,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      useProviders: _useProviders,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      componentContainer: _componentContainer,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      wrapperStyle: _wrapperStyle,
      ...nextComponentProps
    } = nP;

    // Perform shallow comparison
    for (const key in nextComponentProps) {
      if (nextComponentProps[key] !== componentProps[key]) {
        return true;
      }
    }

    return false;
  }

  componentDidUpdate() {
    this.init();
  }

  registerDiv = (ref: HTMLDivElement | null) => {
    this.div = ref;
  };

  private async init() {
    const {
      reactDOM: _reactDOM,
      component: _component,
      useProviders: _useProviders,
      componentContainer: Container = defaultContainer,
      ...componentProps
    } = this.props;

    const reactDOM = _reactDOM ? (await _reactDOM()).default : ReactDOM;
    const RemoteComponent = await _component();

    const Element = _useProviders ? (
      // Container (default to div) added by default to prevent double rendering.
      // Without a concrete element, the root will be the Providers (useProvider = true).
      // Provider is shared, therefore react couldn't get the reference right and might render it twice.
      <Container>
        <Providers>
          <RemoteComponent.default {...componentProps} />
        </Providers>
      </Container>
    ) : (
      <RemoteComponent.default {...componentProps} />
    );

    const rootElement = this.div;

    if (!rootElement) return;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore Cannot find module 'react-dom/client' or its corresponding type declarations.ts(2307)
    if ((reactDOM as typeof import('react-dom/client')).createRoot) {
      // React 18.0.0+
      if (!this.root)
        this.root =
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore Cannot find module 'react-dom/client' or its corresponding type declarations.ts(2307)
          (reactDOM as typeof import('react-dom/client')).createRoot(
            rootElement
          );

      this.root.render(Element);
      this.unMountFn = () => {
        this.root?.unmount();
      };
    } else {
      // React 18.0.0-
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore Property 'render' does not exist on type 'typeof import("/react-dom/client")'.ts(2339)
      reactDOM.render(Element, rootElement);
      this.unMountFn = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Property 'unmountComponentAtNode' does not exist on type 'typeof import("/react-dom/client")'.ts(2339)
        reactDOM.unmountComponentAtNode(rootElement);
      };
    }
  }

  render() {
    const { wrapperStyle } = this.props;

    return <div style={wrapperStyle} ref={this.registerDiv} />;
  }
}
